import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useEPrescriptionLogins,
  useSaveEPrescriptionLogin,
} from '@/settings/services';
import type { EPrescriptionLogin } from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * EPrescription logins editor (PROD-761 commit 2).
 *
 * Extracted from `practitioner-account-tab.tsx` per @samer review on PR
 * #113 — the pattern is reusable across Practitioner and (likely) future
 * Staff or admin surfaces. Keeping it in its own file makes the
 * Account-tab parent easier to read and prevents the eRx logic from
 * getting copy-pasted into the next tab.
 *
 * UX model:
 *   - One row per assigned practice (read from GET endpoint).
 *   - Each row has independent username + password + Save button.
 *   - Per-row Save is dirty-gated (disabled until value differs from the
 *     fetched initial); never re-POSTs unchanged credentials.
 *   - On success, React Query invalidation refetches the list; local row
 *     state re-syncs via useEffect when refreshed values arrive.
 *
 * Security notes (PHI/credential):
 *   - Cleartext password input matches Flutter
 *     `edit_eRx_login_widget.dart` parity.
 *   - HTTPS-only in transit; at-rest storage is the backend's
 *     responsibility, not ours.
 *   - The api-client has NO global request-body logger (verified during
 *     PR #113 review) — passwords are not at risk of console / Sentry
 *     leakage.
 */
export interface EPrescriptionLoginsSectionProps {
  providerId: string;
  canEdit: boolean;
}

export function EPrescriptionLoginsSection({
  providerId,
  canEdit,
}: EPrescriptionLoginsSectionProps) {
  const { data: logins = [], isLoading } = useEPrescriptionLogins(providerId);

  return (
    <>
      <h2 className={SETTINGS_SECTION_TITLE}>
        E-prescription logins
      </h2>
      <PlainCard>
        {isLoading ? (
          <BrandLoading size={48} label="Loading eRx logins…" />
        ) : logins.length === 0 ? (
          <p className="text-sm text-text-muted">
            No e-prescription logins configured for this provider.
          </p>
        ) : (
          <ul className="space-y-4">
            {logins.map((login) => (
              <EPrescriptionLoginRow
                key={`${login.providerId ?? ''}:${login.practiceId}`}
                initial={login}
                canEdit={canEdit}
              />
            ))}
          </ul>
        )}
      </PlainCard>
    </>
  );
}

function EPrescriptionLoginRow({
  initial,
  canEdit,
}: {
  initial: EPrescriptionLogin;
  canEdit: boolean;
}) {
  const [username, setUsername] = useState(initial.username ?? '');
  const [password, setPassword] = useState(initial.password ?? '');
  const saveMutation = useSaveEPrescriptionLogin();

  // Re-sync local state when the upstream list refetches after a save —
  // prevents stale local values from clobbering server truth on the next
  // edit attempt.
  useEffect(() => {
    setUsername(initial.username ?? '');
    setPassword(initial.password ?? '');
  }, [initial.username, initial.password]);

  // Dirty gate — Save is disabled when current value matches the fetched
  // initial. Prevents accidental re-POST of unchanged credentials
  // (silent no-op at best, confusing UX at worst — @samer review #113).
  const isDirty =
    username !== (initial.username ?? '') ||
    password !== (initial.password ?? '');

  const handleSave = () => {
    saveMutation.mutate(
      {
        ...initial,
        username: username || null,
        password: password || null,
      },
      {
        onSuccess: () =>
          toast.success(`Saved eRx login for ${initial.practiceName ?? 'practice'}`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Save failed'),
      },
    );
  };

  return (
    <li data-slot="settings-login-card" className="flex items-start gap-4 rounded-lg border border-border-card p-4">
      <PracticeImage url={initial.practiceImage ?? null} name={initial.practiceName ?? null} />
      <div className="flex-1 min-w-0 space-y-3">
        <h3 data-slot="settings-login-title" className="text-sm font-semibold text-brand-depth truncate">
          {initial.practiceName ?? 'Practice'}
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel stacked>Username</FieldLabel>
            <TextField
              value={username}
              disabled={!canEdit}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div>
            <FieldLabel stacked>Password</FieldLabel>
            <TextField
              type="password"
              value={password}
              disabled={!canEdit}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function PracticeImage({
  url,
  name,
}: {
  url: string | null;
  name: string | null;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt={name ?? ''}
        data-slot="settings-practice-square"
        className="h-12 w-12 rounded object-cover flex-shrink-0"
      />
    );
  }
  const initials = (name ?? '')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div data-slot="settings-practice-square"
      className="h-12 w-12 rounded bg-surface-background text-text-muted flex items-center justify-center text-sm font-bold flex-shrink-0">
      {initials || '?'}
    </div>
  );
}

function PlainCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}
